/*
    SIMPLIFY YOUR FETCHER WITH THIS HELPER
    How to use it :
    Example
    -> import useFetcher from '@Core/helpers/fetcher';
    -> const fetcher = useFetcher();
    -> getList({ fetcher })

    =>
      import qs from 'qs';
      import { HOST_ENDPOINT } from '@Constants';
      
      export const getList = async ({ fetcher, params }) => {
        const resourceURL = new URL(`${HOST_ENDPOINT}/list`);
        resourceURL.search = new URLSearchParams(qs.stringify(params));
        const requestOptions = {
          method: 'GET',
          // body: JSON.stringify(param), // for body
        };
        return await fetcher(resourceURL, requestOptions);
      };
*/

import {
  // useContext,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { UserbalContext, UserContext } from '@Context/User'

const SESSION_REJECT_STATUS = [401, 403, 419, 440, 498]

const getCurrentUserAgent = () => {
  if (typeof navigator === 'undefined') return ''
  return navigator.userAgent || ''
}

const normalizeText = (value = '') => {
  return `${value}`.trim().toLowerCase()
}

export const hasSessionContextMismatch = ({
  isLogin = false,
  boundUserAgent = '',
  boundIp = '',
  currentUserAgent = '',
  currentIp = '',
} = {}) => {
  if (!isLogin) return false

  const normalizedBoundUserAgent = normalizeText(boundUserAgent)
  const normalizedCurrentUserAgent = normalizeText(currentUserAgent)
  const normalizedBoundIp = normalizeText(boundIp)
  const normalizedCurrentIp = normalizeText(currentIp)

  if (
    normalizedBoundUserAgent &&
    normalizedCurrentUserAgent &&
    normalizedBoundUserAgent !== normalizedCurrentUserAgent
  ) {
    return true
  }

  if (normalizedBoundIp && normalizedCurrentIp && normalizedBoundIp !== normalizedCurrentIp) {
    return true
  }

  return false
}

const extractSessionErrorText = (res) => {
  const response = res?.response || {}
  const candidates = [
    response?.message,
    response?.error,
    response?.error_description,
    response?.detail,
    response?.reason,
  ]
  return candidates
    .filter((value) => typeof value === 'string' && value.trim())
    .join(' ')
    .toLowerCase()
}

export const isServerSessionRejected = (res = {}) => {
  if (SESSION_REJECT_STATUS.includes(res?.status)) return true

  const response = res?.response || {}
  const errorCode = `${response?.errorCode || response?.code || ''}`.toUpperCase()
  if (
    ['SESSION_INVALID', 'SESSION_EXPIRED', 'SESSION_REUSED', 'INVALID_SESSION'].includes(
      errorCode
    )
  ) {
    return true
  }

  const errorText = extractSessionErrorText(res)
  if (!errorText) return false
  return (
    errorText.includes('invalid session') ||
    errorText.includes('session expired') ||
    errorText.includes('session cookie') ||
    errorText.includes('reused') ||
    errorText.includes('user-agent mismatch') ||
    errorText.includes('ip mismatch') ||
    errorText.includes('context mismatch')
  )
}

const fetcher = async (url, options) => {
  const pathname = new URL(url).pathname || ''
  const { responseType = '' } = options

  try {
    const res = await fetch(url, options)

    // return response json on success
    if (res.status >= 200 && res.status <= 299) {
      try {
        if (responseType === 'raw') {
          return res
        }
        if (responseType === 'blob') {
          return await res.blob()
        }

        const data = await res.json()
        if (!data) {
          console.error(
            `[API error]: Message: Wrong data format from API,  Path: ${pathname}`
          )
          throw new Error('error not formated json api')
        }
        return data
      } catch (err) {
        return err
      }
    } else if (res.status >= 400 && res.status !== 401 && res.status !== 403) {
      console.error(
        `[API error]: Message: Failed fetching API, Path: ${pathname}, Status: ${res.status}`
      )
    }

    // return object on error
    return {
      url: url,
      is_error: true,
      status: res.status,
      response: await res.json(),
    }
  } catch (err) {
    return {
      is_error: true,
      status: 500,
      error: err,
    }
  }
}

/**
 * Returns required fetch options for all intools API calls,
 * use this if you want to call `fetch` directly, e.g: blob
 *
 * @returns {RequestInit}
 */
export const useFetcherOptions = () => {
  const [userInfo] = UserbalContext()
  const {
    userAccess: {
      access_token: JWT_TOKEN = '',
      jti: sessionId = '',
      isLogin = false,
      bound_user_agent: boundUserAgent = '',
      bound_ip: boundIp = '',
    } = {},
    // accessToken: sonic_access_token,
    // sid: SID_Intools,
  } = userInfo
  const currentUserAgent = getCurrentUserAgent()

  const options = useMemo(
    () => ({
      credentials: 'include',
      headers: {
        Authorization: `${JWT_TOKEN ? `Bearer ${JWT_TOKEN}` : 'NO_JWT_TOKEN'}`,
        'X-Session-Id': sessionId || '',
        'X-Auth-State': isLogin ? 'authenticated' : 'anonymous',
        'X-Session-Bound-User-Agent': boundUserAgent || currentUserAgent,
        ...(boundIp ? { 'X-Session-Bound-IP': boundIp } : {}),
        ...(currentUserAgent ? { 'X-Client-User-Agent': currentUserAgent } : {}),
        // sonic_access_token: sonic_access_token,
        // sid_intools: SID_Intools,
        'Content-Type': 'application/json',
      },
      referrerPolicy: 'no-referrer-when-downgrade',
    }),
    // [JWT_TOKEN, SID_Intools, sonic_access_token]
    [JWT_TOKEN, boundIp, boundUserAgent, currentUserAgent, isLogin, sessionId]
  )

  return options
}

const useFetcher = () => {
  const fetcherOptions = useFetcherOptions()
  const [userInfo, dispatch] = useContext(UserContext)
  const {
    userAccess: { isLogin = false, bound_user_agent: boundUserAgent = '', bound_ip: boundIp = '' } = {},
  } = userInfo || {}

  const fetcherHookCb = useCallback(
    (url, opt = {}, onAPIErrorReload = true) => {
      const currentUserAgent = getCurrentUserAgent()
      if (
        hasSessionContextMismatch({
          isLogin,
          boundUserAgent,
          boundIp,
          currentUserAgent,
        })
      ) {
        dispatch({
          type: 'LOGOUT_USER_DATA',
          payload: {},
        })
        if (onAPIErrorReload) {
          window?.location?.assign('/login')
        }
        return Promise.resolve({
          is_error: true,
          status: 440,
          response: {
            message: 'Session context mismatch. Re-authentication required.',
          },
        })
      }

      const options = {
        ...fetcherOptions,
        ...opt,
        headers: {
          ...fetcherOptions.headers,
          ...(opt.headers || {}),
        },
      }
      return fetcher(url, options).then((res) => {
        if (isServerSessionRejected(res)) {
          dispatch({
            type: 'LOGOUT_USER_DATA',
            payload: {},
          })
          if (onAPIErrorReload) {
            window?.location?.assign('/login')
          }
        }
        return res
      })
    },
    [boundIp, boundUserAgent, dispatch, fetcherOptions, isLogin]
  )

  return fetcherHookCb
}

export default useFetcher
